/**
 * DevModePanel - Development tools for testing
 *
 * Features:
 * - Session context display
 * - Login/Logout buttons that set/clear auth cookie
 * - Member profile preview (full profile with membership, org, tags, etc.)
 * - Cookie inspection
 *
 * Auth flow mirrors production:
 * - Click "Login" sets session cookie with JSON: {"session_id": "demo-01"}
 * - Page refresh picks up cookie and fetches profile from /api/auth/profile
 * - Click "Logout" clears cookie
 */
import { useState } from 'react';
import { useDevMode } from '../context/DevModeContext';
import { getAnonymousUserId } from '../lib/user-context';

export default function DevModePanel({ clientId, sessionId, messageCount = 0 }) {
  const {
    isDevMode,
    panelVisible,
    togglePanel,
    authSessionId,
    isAuthenticated,
    profile,
    profileSource,
    profileLoading,
    profileError,
    login,
    loginWithSSO,
    logout,
    availableSessions,
  } = useDevMode();

  const [showMemberJson, setShowMemberJson] = useState(false);

  // Don't render in production
  if (!isDevMode) return null;

  // Collapsed toggle button
  if (!panelVisible) {
    return (
      <button
        onClick={togglePanel}
        className="fixed top-2 right-2 z-50 p-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg hover:bg-slate-700 transition-colors"
        title="Show Dev Panel"
      >
        🔧 Dev
      </button>
    );
  }

  // Parse cookies for display
  const allCookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [name, value] = cookie.trim().split('=');
    if (name) acc[name] = value;
    return acc;
  }, {});

  // Get analytics data
  const anonymousId = getAnonymousUserId();

  return (
    <div className="mb-6 p-4 bg-slate-100 rounded-lg border border-slate-300 text-xs font-mono relative">
      {/* Close button */}
      <button
        onClick={togglePanel}
        className="absolute top-2 right-2 p-1 text-slate-500 hover:text-slate-700"
        title="Hide Dev Panel"
      >
        ✕
      </button>

      <div className="font-bold mb-3 text-slate-700 flex items-center gap-2">
        <span>🔧</span>
        <span>Dev Mode Panel</span>
      </div>

      {/* Session Context Section */}
      <div className="space-y-1.5 text-slate-600">
        <div className="flex gap-2">
          <span className="font-semibold min-w-28">Client ID:</span>
          <span className="text-indigo-600 font-medium">{clientId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold min-w-28">Session ID:</span>
          <span className="text-green-600 font-medium">{sessionId || 'Not created yet'}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold min-w-28">Anonymous ID:</span>
          <span className="text-blue-600 font-medium">{anonymousId}</span>
        </div>
        {messageCount > 0 && (
          <div className="flex gap-2">
            <span className="font-semibold min-w-28">Messages:</span>
            <span className="text-amber-600 font-medium">{messageCount}</span>
          </div>
        )}
      </div>

      {/* Auth State */}
      <div className="pt-3 mt-3 border-t border-slate-300">
        <div className="font-semibold mb-2 flex items-center gap-2">
          <span>🔐</span>
          <span>Authentication</span>
          {isAuthenticated ? (
            <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded-full">
              Logged In
            </span>
          ) : (
            <span className="ml-auto px-2 py-0.5 bg-slate-200 text-slate-600 text-[10px] font-medium rounded-full">
              Anonymous
            </span>
          )}
        </div>

        {/* Loading state */}
        {profileLoading ? (
          <div className="bg-white rounded border border-slate-200 p-3 animate-pulse">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full" />
              <div className="flex-1">
                <div className="w-32 h-3 bg-slate-200 rounded mb-1" />
                <div className="w-24 h-2 bg-slate-200 rounded" />
              </div>
            </div>
            <div className="mt-2 text-[10px] text-slate-400 text-center">Validating session...</div>
          </div>
        ) : profileError ? (
          <div className="bg-red-50 rounded border border-red-200 p-3">
            <div className="text-red-600 text-[11px] font-medium">Auth Error</div>
            <div className="text-red-500 text-[10px] mt-1">{profileError}</div>
          </div>
        ) : isAuthenticated && profile ? (
          /* Logged in - show profile + logout */
          <div className="bg-white rounded border border-green-200 p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2 items-center">
                <span className="text-lg">👤</span>
                <div>
                  <div className="font-medium text-slate-800">
                    {profile.person?.full_name || profile.user_name}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {profile.person?.email || profile.email}
                  </div>
                </div>
              </div>
              <button
                onClick={logout}
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[10px] rounded transition-colors"
              >
                Logout
              </button>
            </div>

            {/* Cookie info */}
            <div className="text-[10px] text-slate-500 mb-2 p-1.5 bg-slate-50 rounded">
              <span className="font-semibold">Cookie:</span>{' '}
              <code className="text-purple-600">session={authSessionId}</code>
            </div>

            {/* Source badge */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-slate-500">Source:</span>
              <span
                className={`text-[10px] font-medium ${
                  profileSource === 'cache' ? 'text-blue-600' : 'text-green-600'
                }`}
              >
                {profileSource === 'cache' ? 'Cached (20 min TTL)' : 'SSO Validated'}
              </span>
            </div>

            {/* Profile details - Basic */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] mb-2">
              <div>
                <span className="text-slate-500">Member ID:</span>{' '}
                <span className="text-indigo-600 font-medium">
                  {profile.id || profile.member_id}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Tenant:</span>{' '}
                <span className="text-green-600 font-medium">{profile.tenant_id}</span>
              </div>
              {profile.roles && (
                <div className="col-span-2">
                  <span className="text-slate-500">Roles:</span>{' '}
                  <span>{profile.roles.join(', ')}</span>
                </div>
              )}
            </div>

            {/* Membership info */}
            {profile.membership && (
              <div className="text-[10px] p-1.5 bg-indigo-50 rounded mb-2">
                <div className="font-semibold text-indigo-700 mb-1">Membership</div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-slate-600">
                  <div>
                    Type: <span className="font-medium">{profile.membership.member_type_code}</span>
                  </div>
                  <div>
                    Status: <span className="font-medium">{profile.membership.status_code}</span>
                  </div>
                  <div>
                    Member:{' '}
                    <span className="font-medium">
                      {profile.membership.is_member ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    New:{' '}
                    <span className="font-medium">
                      {profile.membership.is_new_member ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Organization info */}
            {profile.organization?.name && (
              <div className="text-[10px] p-1.5 bg-blue-50 rounded mb-2">
                <div className="font-semibold text-blue-700 mb-1">Organization</div>
                <div className="text-slate-600">{profile.organization.name}</div>
              </div>
            )}

            {/* Tenure & engagement */}
            {profile.tenure && (
              <div className="text-[10px] p-1.5 bg-amber-50 rounded mb-2">
                <div className="font-semibold text-amber-700 mb-1">Engagement</div>
                <div className="grid grid-cols-2 gap-x-2 text-slate-600">
                  <div>
                    Tenure: <span className="font-medium">{profile.tenure.years} years</span>
                  </div>
                  <div>
                    Events:{' '}
                    <span className="font-medium">
                      {profile.event_stats?.total_events_attended || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tags */}
            {profile.tags && profile.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {profile.tags.slice(0, 6).map((tag, i) => (
                  <span
                    key={i}
                    className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[9px] rounded"
                    title={tag.tag_value}
                  >
                    {tag.tag_value}
                  </span>
                ))}
                {profile.tags.length > 6 && (
                  <span className="text-[9px] text-slate-400">+{profile.tags.length - 6} more</span>
                )}
              </div>
            )}

            {/* Toggle JSON */}
            <button
              onClick={() => setShowMemberJson(!showMemberJson)}
              className="mt-2 text-[10px] text-indigo-600 hover:text-indigo-800"
            >
              {showMemberJson ? '▼ Hide Full Profile JSON' : '▶ Show Full Profile JSON'}
            </button>

            {showMemberJson && (
              <pre className="mt-2 p-2 bg-slate-50 rounded text-[9px] overflow-x-auto max-h-60 overflow-y-auto">
                {JSON.stringify(profile, null, 2)}
              </pre>
            )}
          </div>
        ) : (
          /* Not logged in - show login options */
          <div className="bg-slate-50 rounded border border-slate-200 p-3">
            <div className="text-center mb-3">
              <div className="text-slate-400 text-sm mb-1">👤</div>
              <div className="text-[11px] text-slate-500">Not authenticated</div>
            </div>

            {/* SSO Login Button (for real SSO testing) */}
            <button
              onClick={loginWithSSO}
              className="w-full mb-3 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium rounded transition-colors"
              title="Login via iMIS SSO (requires Gateway running)"
            >
              🔐 Login with SSO
            </button>

            <div className="relative mb-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300"></div>
              </div>
              <div className="relative flex justify-center text-[10px]">
                <span className="px-2 bg-slate-50 text-slate-400">or use demo profile</span>
              </div>
            </div>

            {/* Profile selector dropdown */}
            <div className="space-y-2">
              <select
                id="demo-profile-select"
                className="w-full px-2 py-1.5 text-[11px] border border-slate-300 rounded bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    login(e.target.value);
                  }
                }}
              >
                <option value="" disabled>
                  Select a demo profile...
                </option>
                {availableSessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.label} - {session.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-[9px] text-slate-400 mt-2 text-center">
              Demo profiles work offline. SSO requires Gateway.
            </div>
          </div>
        )}
      </div>

      {/* Cookie Info (collapsed) */}
      <details className="pt-3 mt-3 border-t border-slate-300">
        <summary className="font-semibold cursor-pointer text-slate-600 hover:text-slate-800">
          🍪 Cookies ({Object.keys(allCookies).length})
        </summary>
        <div className="mt-2 space-y-1">
          {Object.keys(allCookies).length > 0 ? (
            <pre className="bg-white p-2 rounded border border-slate-200 text-[9px] overflow-x-auto max-h-24 overflow-y-auto">
              {JSON.stringify(allCookies, null, 2)}
            </pre>
          ) : (
            <div className="text-slate-500">No cookies set</div>
          )}
        </div>
      </details>
    </div>
  );
}
